import { ActionIcon, Button, Text, TextInput, UnstyledButton } from "@mantine/core";
import { ChevronDown, ChevronUp, Plus, Trash } from "lucide-react";
import { useState } from "react";

import classes from "./ad-copies.module.css";
import type { EditSection } from "./editing";
import { SECTIONS, sectionOf, type TextKey } from "./elements";

export interface CopyEditorProps {
  sections: readonly EditSection[];
  disabled: boolean;
  onChange: (sections: EditSection[]) => void;
}

/**
 * Live's ad copy editor (`edit-text-copy-form`, captures advanced-234/01 to 03 and 21), shared by
 * the Ad copies modal's Edit and the Ad copy tab: each section with a bin (not for the last one),
 * its count and a fold; its numbered values, each with a bin (not for the last one), and "Add
 * Element"; then the sections it lacks as cards under "Additional sections".
 */
export function CopyEditor({ sections, disabled, onChange }: CopyEditorProps) {
  const edit = (key: TextKey, values: (current: string[]) => string[]) =>
    onChange(
      sections.map((section) =>
        section.key === key ? { key, values: values(section.values) } : section,
      ),
    );
  const missing = SECTIONS.filter(({ key }) => !sections.some((section) => section.key === key));
  return (
    <div>
      {sections.map((section) => (
        <SectionValues
          key={section.key}
          section={section}
          disabled={disabled}
          removable={sections.length > 1}
          onRemove={() => onChange(sections.filter((each) => each.key !== section.key))}
          onChange={(values) => edit(section.key, values)}
        />
      ))}
      {missing.length === 0 ? null : (
        <div className={classes.titled}>
          <Text className={classes.sectionTitle}>Additional sections</Text>
          <div className={classes.cards}>
            {missing.map(({ key, label, icon: Icon }) => (
              <UnstyledButton
                key={key}
                className={classes.card}
                disabled={disabled}
                onClick={() => onChange([...sections, { key, values: [""] }])}
              >
                <Icon size={16} strokeWidth={1.5} aria-hidden />
                {label}
              </UnstyledButton>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface SectionValuesProps {
  section: EditSection;
  disabled: boolean;
  removable: boolean;
  onRemove: () => void;
  onChange: (values: (current: string[]) => string[]) => void;
}

/** One section's values (live's `text-element-editor`), open at first. */
function SectionValues({ section, disabled, removable, onRemove, onChange }: SectionValuesProps) {
  const [open, setOpen] = useState(true);
  const { label, icon: Icon } = sectionOf(section.key);
  const fold = () => setOpen((current) => !current);
  return (
    <section className={classes.group} aria-label={label}>
      <div className={classes.groupHeader}>
        <UnstyledButton className={classes.groupLabel} aria-expanded={open} onClick={fold}>
          <Icon size={20} strokeWidth={1.5} aria-hidden />
          {label}
        </UnstyledButton>
        <div className={classes.groupRight}>
          {open ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Remove ${label}`}
              disabled={disabled || !removable}
              onClick={onRemove}
            >
              <Trash size={16} strokeWidth={1.5} aria-hidden />
            </ActionIcon>
          ) : null}
          <Text className={classes.count}>{section.values.length}</Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={open ? `Fold ${label}` : `Open ${label}`}
            onClick={fold}
          >
            {open ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
          </ActionIcon>
        </div>
      </div>
      {open ? (
        <div className={classes.values}>
          {section.values.map((value, at) => (
            <div key={at} className={classes.editorRow}>
              <Text className={classes.editorNumber}>{at + 1}</Text>
              <TextInput
                style={{ flex: 1 }}
                aria-label={`${label} ${at + 1}`}
                placeholder="Enter text..."
                value={value}
                disabled={disabled}
                onChange={(event) => {
                  const text = event.currentTarget.value;
                  onChange((current) => current.map((each, index) => (index === at ? text : each)));
                }}
              />
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Remove ${label} ${at + 1}`}
                disabled={disabled || section.values.length === 1}
                onClick={() => onChange((current) => current.filter((_, index) => index !== at))}
              >
                <Trash size={16} strokeWidth={1.5} aria-hidden />
              </ActionIcon>
            </div>
          ))}
          <Button
            variant="subtle"
            leftSection={<Plus size={20} strokeWidth={1.5} />}
            disabled={disabled}
            onClick={() => onChange((current) => [...current, ""])}
            w="fit-content"
          >
            Add Element
          </Button>
        </div>
      ) : null}
    </section>
  );
}
